import React, { useState, useContext, useEffect } from 'react';
import './App.css';
import TransactionGraph from './components/TransactionGraph';
import SearchBar from './components/SearchBar';
import TransactionDetails from './components/TransactionDetails';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import UserProfile from './components/UserProfile';
import TraceModeSelector from './components/TraceModeSelector';
import TraceStatistics from './components/TraceStatistics';
import EntityDetailsModal from './components/EntityDetailsModal';
import TraceProgress from './components/TraceProgress';
import AddressTimeline from './components/AddressTimeline';
import AddressFilters from './components/AddressFilters';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { useTraceTask } from './hooks/useTraceTask';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AppContent() {
  const { user, logout, loading: authLoading, isAuthenticated, isAdmin } = useContext(AuthContext);
  const [graphData, setGraphData] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showOutputSelector, setShowOutputSelector] = useState(false);
  const [currentQuery, setCurrentQuery] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [traceMode, setTraceMode] = useState({ mode: 'smart', maxDepth: 5 });
  const [traceData, setTraceData] = useState(null);
  const [searchType, setSearchType] = useState(null); // 'transaction' or 'address'
  const [timelineData, setTimelineData] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [addressFilters, setAddressFilters] = useState({ limit: 100, days: null });
  const [currentAddress, setCurrentAddress] = useState(null);
  
  // Background task hook
  const { 
    startTrace, 
    reset: resetTrace, 
    status, 
    progress, 
    currentStep, 
    result, 
    error: traceError, 
    isPolling 
  } = useTraceTask();

  // Reset state when user changes (login/logout)
  useEffect(() => {
    setGraphData(null);
    setSelectedTransaction(null);
    setError(null);
    setShowOutputSelector(false);
    setCurrentQuery(null);
  }, [user?.id]); // Reset when user ID changes

  // Handle completed background trace result
  useEffect(() => {
    if (result) {
      setLoading(false);
      setTraceData(result);
      
      // Convert result to graph based on trace type
      if (result.path) {
        // Smart trace - single path
        convertPathToGraph(result.path, currentQuery?.query);
      } else if (result.paths) {
        // Multi-path or tree trace
        convertMultiPathToGraph(result.paths, currentQuery?.query);
      }
    }
  }, [result]);

  // Handle background trace error
  useEffect(() => {
    if (traceError) {
      setError(traceError);
      setLoading(false);
    }
  }, [traceError]);

  if (authLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const handleSearch = async (query, type) => {
    setLoading(true);
    setError(null);
    setGraphData(null);
    setSelectedTransaction(null);
    setShowOutputSelector(false);
    setTimelineData(null);
    setSearchType(type);

    try {
      if (type === 'transaction') {
        // First, fetch transaction details to show outputs
        const txResponse = await axios.get(`${API_URL}/api/transaction/${query}`);
        setSelectedTransaction(txResponse.data);
        
        // If transaction has multiple outputs, show selector
        if (txResponse.data.outputs && txResponse.data.outputs.length > 1) {
          setShowOutputSelector(true);
          setCurrentQuery({ query, type });
          setLoading(false);
          return;
        }
        
        // Single output or no outputs - trace directly
        await traceTransaction(query);
        
      } else if (type === 'address') {
        // Save address for filter updates
        setCurrentAddress(query);
        // Get address timeline with current filters
        await fetchAddressTimeline(query, addressFilters);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Fehler beim Laden der Daten';
      
      // Check for rate limit errors
      if (errorMsg.includes('rate limit') || errorMsg.includes('429') || err.response?.status === 429) {
        setError('⏱️ API Rate Limit erreicht. Bitte warte 30 Sekunden und versuche es erneut.');
      } else if (errorMsg.includes('not found') || err.response?.status === 404) {
        setError('❌ Transaktion oder Adresse nicht gefunden. Bitte überprüfe deine Eingabe.');
      } else {
        setError(errorMsg);
      }
      
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddressTimeline = async (address, filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.days) params.append('days', filters.days);
      
      const response = await axios.get(`${API_URL}/address/${address}/timeline?${params.toString()}`);
      setTimelineData(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Fehler beim Laden der Timeline';
      setError(errorMsg);
      console.error('Error fetching timeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setAddressFilters(newFilters);
    if (currentAddress) {
      fetchAddressTimeline(currentAddress, newFilters);
    }
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    // Clear data when switching types
    if (type === 'transaction') {
      setTimelineData(null);
    } else {
      setGraphData(null);
      setTraceData(null);
    }
  };

  const traceTransaction = async (txid) => {
    setLoading(true);
    setTraceData(null);
    resetTrace(); // Reset any previous background trace
    
    try {
      // Determine if we should use background tasks based on settings
      // Use background tasks for deep traces (depth > 5) or multi-path traces
      const shouldUseBackground = (
        (traceMode.mode === 'smart' && traceMode.maxDepth > 5) ||
        (traceMode.mode === 'multi' && (traceMode.maxDepth > 5 || traceMode.numPaths > 3)) ||
        (traceMode.mode === 'tree' && (traceMode.maxDepth > 3 || traceMode.maxTotalPaths > 20))
      );

      if (shouldUseBackground) {
        // Use background task for long-running traces
        await startTrace(txid, traceMode.mode, {
          maxDepth: traceMode.maxDepth,
          numPaths: traceMode.numPaths,
          useWalletExplorer: traceMode.useWalletExplorer
        });
        // Loading will be handled by polling hook
        
      } else {
        // Use direct API call for quick traces
        let response;
        const walletExplorerParam = traceMode.useWalletExplorer ? `&use_wallet_explorer=true` : '';
        
        if (traceMode.mode === 'smart') {
          // Smart trace - single best path
          response = await axios.get(
            `${API_URL}/api/trace/${txid}/smart?max_depth=${traceMode.maxDepth || 5}${walletExplorerParam}`
          );
          setTraceData(response.data);
          // Convert to graph format for visualization
          convertPathToGraph(response.data.path, txid);
          
        } else if (traceMode.mode === 'multi') {
          // Multi-path trace
          response = await axios.get(
            `${API_URL}/api/trace/${txid}/multi-path?num_paths=${traceMode.numPaths || 3}&max_depth=${traceMode.maxDepth || 5}${walletExplorerParam}`
          );
          setTraceData(response.data);
          // Convert paths to graph
          convertMultiPathToGraph(response.data.paths, txid);
          
        } else if (traceMode.mode === 'tree') {
          // Tree trace
          response = await axios.get(
            `${API_URL}/api/trace/${txid}/tree?branches_per_level=${traceMode.branches || 2}&max_depth=${traceMode.maxDepth || 3}&max_total_paths=${traceMode.maxTotalPaths || 20}${walletExplorerParam}`
          );
          setTraceData(response.data);
          // Convert tree to graph
          convertMultiPathToGraph(response.data.paths, txid);
          
        } else {
          // Full trace - original graph mode
          response = await axios.get(
            `${API_URL}/api/trace/${txid}?max_depth=${traceMode.maxDepth || 5}`
          );
          setGraphData(response.data);
        }
        setLoading(false);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Fehler beim Tracen';
      setError(errorMsg);
      setLoading(false);
    }
  };

  const convertPathToGraph = (path, rootTxid) => {
    // Convert single path to graph format
    const nodes = [];
    const edges = [];
    
    // Build map of tainted addresses for quick lookup
    const taintedMap = new Map();
    if (path.tainted_addresses) {
      path.tainted_addresses.forEach(tainted => {
        taintedMap.set(tainted.address, tainted);
      });
    }
    
    // Add transactions as nodes
    path.transactions.forEach((txid, idx) => {
      // Find known entity for this address
      const address = path.addresses[idx];
      const entity = path.known_entities?.find(e => e.address === address);
      const taintInfo = taintedMap.get(address);
      
      let label = `TX ${idx}\n${txid.substring(0, 8)}...`;
      
      // Tainted addresses get priority in labeling
      if (taintInfo) {
        const taintIcon = taintInfo.verified ? '🚨' : '⚠️';
        const shortDesc = taintInfo.description || taintInfo.category;
        label = `${taintIcon} ${shortDesc}\n${txid.substring(0, 8)}...`;
      } else if (entity) {
        const icon = entity.entity_type === 'exchange' ? '🏦' : 
                     entity.entity_type === 'mining_pool' ? '⛏️' :
                     entity.entity_type === 'mixer' ? '🌪️' :
                     entity.entity_type === 'darknet' ? '🕵️' :
                     entity.entity_type === 'known_hack' ? '🚨' : '🏷️';
        label = `${icon} ${entity.label}\n${txid.substring(0, 8)}...`;
      }
      
      nodes.push({
        id: txid,
        label: label,
        type: 'transaction',
        depth: idx,
        is_coinbase: idx === path.transactions.length - 1 && path.coinbase_reached,
        total_value: idx === path.transactions.length - 1 ? path.total_value : 0,
        known_entity: entity,
        tainted_info: taintInfo,  // NEW: Add taint info to node
        address: address
      });
      
      if (idx > 0) {
        edges.push({
          from: txid,
          to: path.transactions[idx - 1],
          label: ''
        });
      }
    });
    
    // ADD OUTPUT NODES for tainted addresses that are outputs (not in the input chain)
    if (path.tainted_addresses) {
      path.tainted_addresses.forEach((tainted, taintIdx) => {
        // Only add if it's an output and not already in the transaction chain
        if (tainted.direction === 'output' && !path.addresses.includes(tainted.address)) {
          const taintIcon = tainted.verified ? '🚨' : '⚠️';
          const shortDesc = tainted.description || tainted.category;
          const nodeId = `output-${tainted.address}`;
          
          nodes.push({
            id: nodeId,
            label: `${taintIcon} ${shortDesc}\n${tainted.address.substring(0, 8)}...`,
            type: 'output_address',
            depth: tainted.depth + 0.5,  // Place between transaction and next
            is_coinbase: false,
            total_value: tainted.value_btc || 0,
            known_entity: null,
            tainted_info: tainted,
            address: tainted.address
          });
          
          // Connect from the transaction at that depth to this output
          const sourceTxid = path.transactions[tainted.depth];
          if (sourceTxid) {
            edges.push({
              from: sourceTxid,
              to: nodeId,
              label: `${tainted.value_btc?.toFixed(4) || '?'} BTC`,
              path_type: 'output'
            });
          }
        }
      });
    }
    
    setGraphData({ nodes, edges, root_txid: rootTxid });
  };

  const convertMultiPathToGraph = (paths, rootTxid) => {
    // Convert multiple paths to graph format with different colors
    const nodes = new Map();
    const edges = [];
    
    // Collect all known entities from all paths
    const allEntities = new Map();
    paths.forEach(path => {
      path.known_entities?.forEach(entity => {
        allEntities.set(entity.address, entity);
      });
    });
    
    // Collect all tainted addresses from all paths
    const allTainted = new Map();
    paths.forEach(path => {
      path.tainted_addresses?.forEach(tainted => {
        allTainted.set(tainted.address, tainted);
      });
    });
    
    paths.forEach((path, pathIdx) => {
      path.transactions.forEach((txid, idx) => {
        const address = path.addresses[idx];
        const entity = allEntities.get(address);
        const taintInfo = allTainted.get(address);
        
        let label = `TX\n${txid.substring(0, 8)}...`;
        
        // Tainted addresses get priority in labeling
        if (taintInfo) {
          const taintIcon = taintInfo.verified ? '🚨' : '⚠️';
          const shortDesc = taintInfo.description || taintInfo.category;
          label = `${taintIcon} ${shortDesc}\n${txid.substring(0, 8)}...`;
        } else if (entity) {
          const icon = entity.entity_type === 'exchange' ? '🏦' : 
                       entity.entity_type === 'mining_pool' ? '⛏️' :
                       entity.entity_type === 'mixer' ? '🌪️' :
                       entity.entity_type === 'darknet' ? '🕵️' :
                       entity.entity_type === 'known_hack' ? '🚨' : '🏷️';
          label = `${icon} ${entity.label}\n${txid.substring(0, 8)}...`;
        }
        
        if (!nodes.has(txid)) {
          nodes.set(txid, {
            id: txid,
            label: label,
            type: 'transaction',
            depth: idx,
            is_coinbase: idx === path.transactions.length - 1 && path.coinbase_reached,
            total_value: path.total_value,
            path_type: path.path_type,
            path_idx: pathIdx,
            known_entity: entity,
            tainted_info: taintInfo,  // NEW: Add taint info to node
            address: address
          });
        } else {
          // Node exists from another path - update entity or taint if found
          const existingNode = nodes.get(txid);
          
          // Taint info takes priority
          if (taintInfo && !existingNode.tainted_info) {
            existingNode.tainted_info = taintInfo;
            existingNode.label = label;
            existingNode.address = address;
          } else if (entity && !existingNode.known_entity && !existingNode.tainted_info) {
            existingNode.known_entity = entity;
            existingNode.label = label;
            existingNode.address = address;
          }
        }
        
        if (idx > 0) {
          const edgeKey = `${txid}-${path.transactions[idx - 1]}`;
          if (!edges.some(e => e.from === txid && e.to === path.transactions[idx - 1])) {
            edges.push({
              from: txid,
              to: path.transactions[idx - 1],
              label: pathIdx === 0 ? '⭐' : `${pathIdx + 1}`,
              path_type: path.path_type,
              path_idx: pathIdx
            });
          }
        }
      });
    });
    
    // ADD OUTPUT NODES for tainted addresses that are outputs
    // Process tainted output addresses across all paths
    paths.forEach((path, pathIdx) => {
      if (path.tainted_addresses) {
        path.tainted_addresses.forEach((tainted) => {
          // Only add if it's an output and not already in the chain
          if (tainted.direction === 'output' && !path.addresses.includes(tainted.address)) {
            const nodeId = `output-${tainted.address}`;
            
            // Check if already added from another path
            if (!nodes.has(nodeId)) {
              const taintIcon = tainted.verified ? '🚨' : '⚠️';
              const shortDesc = tainted.description || tainted.category;
              
              nodes.set(nodeId, {
                id: nodeId,
                label: `${taintIcon} ${shortDesc}\n${tainted.address.substring(0, 8)}...`,
                type: 'output_address',
                depth: tainted.depth + 0.5,
                is_coinbase: false,
                total_value: tainted.value_btc || 0,
                path_type: path.path_type,
                path_idx: pathIdx,
                known_entity: null,
                tainted_info: tainted,
                address: tainted.address
              });
              
              // Connect from source transaction
              const sourceTxid = path.transactions[tainted.depth];
              if (sourceTxid) {
                edges.push({
                  from: sourceTxid,
                  to: nodeId,
                  label: `${tainted.value_btc?.toFixed(4) || '?'} BTC`,
                  path_type: 'output',
                  path_idx: pathIdx
                });
              }
            }
          }
        });
      }
    });
    
    setGraphData({ 
      nodes: Array.from(nodes.values()), 
      edges, 
      root_txid: rootTxid 
    });
  };

  const handleOutputSelect = async (outputIndex) => {
    if (!selectedTransaction || !currentQuery) return;
    
    setShowOutputSelector(false);
    
    // Output selection is just for information
    // We trace ALL inputs regardless of which output was selected
    await traceTransaction(currentQuery.query);
  };

  const handleTraceAll = async () => {
    if (!currentQuery) return;
    
    setShowOutputSelector(false);
    // Same as selecting an output - trace all inputs
    await traceTransaction(currentQuery.query);
  };

  const handleNodeClick = async (nodeId) => {
    try {
      // Check if this is an output address node (not a transaction)
      if (nodeId.startsWith('output-')) {
        // This is a tainted output address node
        if (graphData && graphData.nodes) {
          const node = graphData.nodes.find(n => n.id === nodeId);
          if (node && node.tainted_info) {
            // Create a pseudo-entity object for the modal
            const taintedEntity = {
              address: node.address,
              label: node.tainted_info.description || node.tainted_info.category,
              entity_type: node.tainted_info.category,
              risk_level: 'high_risk',
              source: node.tainted_info.source || 'Taint Database',
              tags: node.tainted_info.tags || [],
              verified: node.tainted_info.verified,
              severity: node.tainted_info.severity,
              category_icon: node.tainted_info.category_icon,
              direction: node.tainted_info.direction,
              value_btc: node.tainted_info.value_btc,
              depth: node.tainted_info.depth,
              is_tainted: true
            };
            setSelectedEntity(taintedEntity);
          }
        }
        return;
      }
      
      // Regular transaction node
      const response = await axios.get(`${API_URL}/api/transaction/${nodeId}`);
      setSelectedTransaction(response.data);
      
      // Then check if this node has an entity and show modal
      if (graphData && graphData.nodes) {
        const node = graphData.nodes.find(n => n.id === nodeId);
        if (node && node.known_entity) {
          // Show entity modal in addition to transaction details
          setSelectedEntity(node.known_entity);
        } else if (node && node.tainted_info) {
          // Also handle tainted addresses in the input chain
          const taintedEntity = {
            address: node.address,
            label: node.tainted_info.description || node.tainted_info.category,
            entity_type: node.tainted_info.category,
            risk_level: 'high_risk',
            source: node.tainted_info.source || 'Taint Database',
            tags: node.tainted_info.tags || [],
            verified: node.tainted_info.verified,
            severity: node.tainted_info.severity,
            category_icon: node.tainted_info.category_icon,
            direction: node.tainted_info.direction,
            value_btc: node.tainted_info.value_btc,
            depth: node.tainted_info.depth,
            is_tainted: true
          };
          setSelectedEntity(taintedEntity);
        }
      }
    } catch (err) {
      console.error('Error fetching transaction:', err);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div>
            <h1>🪙 Bitcoin Transaction Tracker</h1>
            <p>Verfolge Bitcoin-Transaktionen zurück bis zum Mining Block</p>
          </div>
          <div className="header-buttons">
            <button className="profile-btn" onClick={() => setShowUserProfile(true)}>
              👤 Profil
            </button>
            {isAdmin && (
              <button className="admin-btn" onClick={() => setShowAdminPanel(true)}>
                👥 User Management
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="App-main">
        <SearchBar 
          onSearch={handleSearch} 
          onTypeChange={handleSearchTypeChange}
          loading={loading} 
        />

        {/* Show TraceModeSelector only for transactions or initially */}
        {(searchType === null || searchType === 'transaction') && !showOutputSelector && !loading && !isPolling && (
          <TraceModeSelector 
            onModeChange={setTraceMode}
            loading={loading}
          />
        )}

        {/* Show AddressFilters only for addresses */}
        {searchType === 'address' && !loading && (
          <AddressFilters 
            onFilterChange={handleFilterChange}
            loading={loading}
          />
        )}

        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {/* Background trace progress */}
        {isPolling && (
          <TraceProgress 
            status={status}
            progress={progress}
            currentStep={currentStep}
            error={traceError}
          />
        )}

        {loading && !isPolling && (
          <div className="loading-message">
            <div className="spinner"></div>
            <p>Lade Transaktionsdaten...</p>
            <small style={{color: '#888', marginTop: '0.5rem'}}>
              Dies kann bis zu 30 Sekunden dauern wegen API Rate Limits
            </small>
          </div>
        )}

        {showOutputSelector && selectedTransaction && (
          <div className="output-selector">
            <h2>📤 Outputs dieser Transaktion</h2>
            <p>Diese Transaktion hat {selectedTransaction.outputs.length} Outputs.</p>
            <p><strong>💡 Info:</strong> Bitcoin summiert alle Inputs und verteilt sie auf Outputs. Der Trace folgt <strong>allen Inputs</strong> zurück zum Ursprung.</p>
            
            <div className="output-list">
              {selectedTransaction.outputs.map((output, index) => (
                <button
                  key={index}
                  className="output-item"
                  onClick={() => handleOutputSelect(index)}
                >
                  <div className="output-header">
                    <span className="output-number">Output #{index}</span>
                    <span className="output-value">{output.value.toFixed(8)} BTC</span>
                  </div>
                  <div className="output-address">
                    {output.address || 'Unbekannte Adresse'}
                  </div>
                </button>
              ))}
            </div>
            
            <div className="output-actions">
              <button className="trace-all-btn" onClick={handleTraceAll}>
                🔍 Trace starten - Alle Inputs zurückverfolgen
              </button>
            </div>
          </div>
        )}

        <div className="content-container">
          {searchType === 'address' && timelineData && (
            <div className="timeline-container">
              <AddressTimeline timelineData={timelineData} />
            </div>
          )}

          {searchType === 'transaction' && graphData && (
            <div className="graph-container">
              <TransactionGraph 
                data={graphData} 
                onNodeClick={handleNodeClick}
              />
            </div>
          )}

          {searchType === 'transaction' && selectedTransaction && (
            <div className="details-container">
              <TransactionDetails transaction={selectedTransaction} />
            </div>
          )}
        </div>

        {traceData && (
          <TraceStatistics 
            traceData={traceData}
            mode={traceData.mode}
          />
        )}
      </main>

      <footer className="App-footer">
        <div className="footer-content">
          <p>Made with ❤️ for Bitcoin enthusiasts</p>
          <div className="user-info">
            <span>👤 {user?.username}</span>
            {user?.is_admin && <span className="admin-badge">Admin</span>}
            <button onClick={logout} className="logout-btn">Logout</button>
          </div>
        </div>
      </footer>

      {showAdminPanel && <AdminPanel onClose={() => setShowAdminPanel(false)} />}
      {showUserProfile && <UserProfile onClose={() => setShowUserProfile(false)} />}
      {selectedEntity && (
        <EntityDetailsModal 
          entity={selectedEntity} 
          onClose={() => setSelectedEntity(null)} 
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
